import { Button } from '@pms-digital/design-system';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface ExportSegmentedControlProps<T extends string> {
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
}

/**
 * A labelled row of mutually exclusive toggle buttons — the export panel's
 * idiom for short enumerations (2-3 values), like the footer's format picker
 * so the whole sidebar reads as one control family. The picked value is the
 * filled (`secondary`) button, the others are outlined (`primary`).
 */
export function ExportSegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: ExportSegmentedControlProps<T>) {
  return (
    <fieldset className="space-y-1">
      <legend className="mb-1 text-small">{label}</legend>
      <div className="flex gap-1">
        {options.map((option) => (
          <div key={option.value} className="min-w-0 flex-1">
            <Button
              type="button"
              size="xs"
              fullWidth
              variant={value === option.value ? 'secondary' : 'primary'}
              aria-pressed={value === option.value}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </Button>
          </div>
        ))}
      </div>
    </fieldset>
  );
}
